import { useEffect, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { ROLE_TYPES } from "@/lib/workspace";
import { toast } from "sonner";
import { UserPlus, X, AlertTriangle } from "lucide-react";

const inputCls = "w-full border border-[#15233B]/15 rounded px-2.5 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1E73C9]/30";
const labelCls = "text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60";

export default function ProspectDialog({ prospect, users, partners, efforts, defaultOwnerId, onClose, onSaved }) {
  const editing = !!prospect;
  const [f, setF] = useState({
    full_name: prospect?.full_name || "", email: prospect?.email || "", phone: prospect?.phone || "",
    position: prospect?.position || "", county: prospect?.county || "",
    referral_partner_id: prospect?.referral_partner_id || "", marketing_effort_id: prospect?.marketing_effort_id || "",
    owner_id: prospect?.owner_id || defaultOwnerId || "", notes: prospect?.notes || "",
  });
  const [dup, setDup] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    const name = f.full_name.trim();
    if (name.length < 3 && !f.email.includes("@") && f.phone.replace(/\D/g, "").length < 10) { setDup([]); return; }
    const t = setTimeout(() => {
      api.get("/cards/check-duplicate", { params: {
        full_name: name.length >= 3 ? name : undefined,
        email: f.email.includes("@") ? f.email.trim() : undefined,
        phone: f.phone.replace(/\D/g, "").length >= 10 ? f.phone : undefined,
        pipeline: "recruiting",
      } }).then(({ data }) => setDup(data.matches || [])).catch(() => setDup([]));
    }, 350);
    return () => clearTimeout(t);
  }, [f.full_name, f.email, f.phone]);

  const submit = async (e) => {
    e.preventDefault();
    if (f.full_name.trim().length < 2) { setErr("Name must be at least 2 characters"); return; }
    setBusy(true); setErr("");
    const body = { ...f, referral_partner_id: f.referral_partner_id || null, marketing_effort_id: f.marketing_effort_id || null, owner_id: f.owner_id || null };
    try {
      const { data } = editing ? await api.patch(`/prospects/${prospect.id}`, body) : await api.post("/prospects", body);
      toast.success(editing ? "Prospect saved" : `${data.full_name} added to prospects`);
      onSaved?.(data);
      onClose();
    } catch (e2) { setErr(formatApiError(e2)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#15233B]/50 flex items-center justify-center p-4" onClick={onClose}>
      <form data-testid="prospect-dialog" onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-[#15233B]/10 flex items-center justify-between bg-[#F6F3EC]">
          <div className="flex items-center gap-2">
            <UserPlus className="h-4 w-4 text-[#1E73C9]" />
            <h2 className="text-base font-extrabold text-[#15233B]">{editing ? "Edit prospect" : "Add prospect"}</h2>
          </div>
          <button type="button" data-testid="prospect-dialog-close" onClick={onClose} className="p-1.5 rounded hover:bg-[#15233B]/5"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className={labelCls}>Full name</label>
            <input data-testid="prospect-name" autoFocus value={f.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="e.g., Maria Lopez" className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Email</label>
              <input data-testid="prospect-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="name@email.com" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Phone</label>
              <input data-testid="prospect-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(414) 555-0100" className={inputCls} />
            </div>
          </div>
          {!editing && dup.length > 0 && (
            <div data-testid="prospect-duplicate-warning" className="flex gap-2 text-xs text-[#7a5a1a] bg-[#FFF9C4] border border-[#FFCB05]/50 rounded px-2.5 py-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <div>Already in Recruiting: {dup.slice(0, 3).map((d) => `${d.full_name} (${d.av_id}, matches ${d.match_on.join(", ")})`).join("; ")}. You can still add them.</div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Role interest</label>
              <select data-testid="prospect-position" value={f.position} onChange={(e) => set("position", e.target.value)} className={inputCls}>
                <option value="">— not sure —</option>
                {(f.position && !ROLE_TYPES.includes(f.position) ? [...ROLE_TYPES, f.position] : ROLE_TYPES).map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>County</label>
              <input data-testid="prospect-county" value={f.county} onChange={(e) => set("county", e.target.value)} placeholder="Milwaukee" className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Source</label>
              <select data-testid="prospect-source" value={f.referral_partner_id} onChange={(e) => set("referral_partner_id", e.target.value)} className={inputCls}>
                <option value="">— none —</option>
                {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Event / campaign</label>
              <select data-testid="prospect-effort" value={f.marketing_effort_id} onChange={(e) => set("marketing_effort_id", e.target.value)} className={inputCls}>
                <option value="">— none —</option>
                {efforts.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Owner</label>
            <select data-testid="prospect-owner" value={f.owner_id} onChange={(e) => set("owner_id", e.target.value)} className={inputCls}>
              <option value="">— unassigned —</option>
              {users.filter((u) => u.role !== "field_agent").map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Notes</label>
            <textarea data-testid="prospect-notes" rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Where you met, what they're interested in…" className={inputCls} />
          </div>
          {err && <div data-testid="prospect-error" className="text-xs text-[#b91c1c] bg-[#b91c1c]/5 border border-[#b91c1c]/20 rounded px-2.5 py-2">{err}</div>}
        </div>
        <div className="px-5 py-4 border-t border-[#15233B]/10 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 rounded text-sm font-semibold text-[#15233B] hover:bg-[#15233B]/5">Cancel</button>
          <button type="submit" data-testid="prospect-submit" disabled={busy} className="px-4 py-2 rounded bg-[#1E73C9] text-white text-sm font-bold hover:bg-[#15233B] disabled:opacity-60">
            {busy ? "Saving…" : editing ? "Save" : "Add prospect"}
          </button>
        </div>
      </form>
    </div>
  );
}
